import {
  useApiLoader,
  type UseApiLoaderResponse,
} from "../../../../../../../server/api/hooks/use-api-loader";
import {
  TeamsEngineSevice,
  type TeamsEngineV2BuildMessageRequest,
  type TeamsEngineV2Message,
} from "../../../../../../../server/api/teams-engine-v2";
import { useToken } from "../../../../../../../server/auth/use-token";
import { Waiting } from "../../../../../../../server/supabase/hooks/use-supabase-loader";
import { useMessageBodyCreator } from "../../../../../../message/editor/utils/create-message-body/create-message-body";
import { usePreviewContext } from "../preview-context";
import { useEmailPreviewSource } from "./use-email-preview-source";
import { usePreviewTags } from "./use-preview-tags";
import { extractSurveyIdFromDesign, useSurveyPreviewData } from "./use-survey-preview-data";

export function useBuiltTeamsMessage(): UseApiLoaderResponse<
  TeamsEngineV2Message["attachments"][number]["content"] | null
> {
  const {
    moment_preview_data: { design: template, account_id, poll_ids, dynamic_segment_id_map },
    preview_info,
    preview_test_data,
  } = usePreviewContext();

  const {
    design,
    subject,
    additional_merge_fields: email_additional_merge_fields,
    use_unlayer_email_preview,
  } = useEmailPreviewSource();

  const getToken = useToken();
  const { loading: createMessageBodyLoading, data: createMessageBody } = useMessageBodyCreator();

  const { loading: mergeFieldsLoading, data: mergeFields } = usePreviewTags({
    dynamic_segment_id_map,
    preview_info,
    preview_test_data,
    design,
    subject,
    email_additional_merge_fields,
    use_unlayer_email_preview,
  });

  const surveyId = extractSurveyIdFromDesign(template);
  const { loading: surveyLoading, data: surveyData } = useSurveyPreviewData({
    body: surveyId ? `{{#each survey_result_${surveyId}}}` : undefined,
  });

  return useApiLoader(async () => {
    if (createMessageBodyLoading || mergeFieldsLoading || surveyLoading) throw new Waiting();
    const token = await getToken();
    if (!createMessageBody) throw new Error("No message body creator");

    const { data: messageBodyData, errorMessage } = await createMessageBody({
      editorState: template,
      channel: "teams",
      format: "chat",
      account_id,
      poll_ids: poll_ids ?? [],
    });

    if (errorMessage || !messageBodyData?.body)
      throw new Error(errorMessage ?? "Error creating message body");

    const service = new TeamsEngineSevice(token);

    const surveyMergeFields = surveyData
      ? ({ [surveyData.key]: surveyData.value } as unknown as Record<string, string | null>)
      : {};

    const response = await service.buildMessage({
      body: JSON.parse(messageBodyData.body) as TeamsEngineV2BuildMessageRequest["body"],
      mail_merge_fields: {
        ...Object.fromEntries(
          (mergeFields?.tags ?? []).map((field) => [field.key, field.value ?? ""]),
        ),
        ...surveyMergeFields,
      },
      dynamic_condition_fields: mergeFields?.dynamic_condition_fields ?? {},
    });

    if (response.error) throw new Error(response.error);
    return response.data?.attachments?.[0]?.content ?? null;
  }, [
    createMessageBodyLoading,
    mergeFieldsLoading,
    getToken,
    createMessageBody,
    template,
    account_id,
    mergeFields?.tags,
    mergeFields?.dynamic_condition_fields,
    poll_ids,
    surveyLoading,
    surveyData,
  ]);
}
